'use client';

import Link from '~/(tenant)/_components/tenant-link';

import '../styles.css';

const LOGO_SRC = '/landing-pages/padeldistrict/logos/wordmark-en-white.svg';
const WHATSAPP_HREF = 'https://wa.me/966500191234';
const BOOK_HREF = '/book';
const CONTACT_HREF = '/contact';

function Logo({ className }: { className?: string }) {
  return (
    <img
      src={LOGO_SRC}
      alt="Padel District"
      className={className}
      onError={(e) => {
        const img = e.target as HTMLImageElement;
        img.style.display = 'none';
        const span = document.createElement('span');
        span.style.cssText =
          "font-family:'Barlow Condensed',sans-serif;font-size:1.1rem;font-weight:800;letter-spacing:0.04em;color:#fff;text-transform:uppercase;";
        span.textContent = 'PADEL DISTRICT';
        img.parentElement?.appendChild(span);
      }}
    />
  );
}

// Section anchors live on the landing page only — prefix with `/` so they
// navigate back to `/` (then scroll) when clicked from a sub-route.
const navigateLinks = [
  { label: 'Book a Court', href: BOOK_HREF, internal: true },
  { label: 'Gaby Reca Academy', href: '/#academy', internal: false },
  { label: 'Socials & Events', href: '/#events', internal: false },
  { label: 'Membership', href: '/', internal: true },
  { label: 'Partners', href: '/#partners', internal: false },
  { label: 'Location', href: '/#location', internal: false },
];

const connectLinks = [
  { label: 'WhatsApp Help', href: WHATSAPP_HREF, internal: false },
  { label: 'Become a Partner', href: CONTACT_HREF, internal: true },
  { label: 'Join the Team', href: CONTACT_HREF, internal: true },
  { label: 'Privacy Policy', href: '/privacy-policy', internal: true },
  { label: 'Terms of Service', href: '/terms-of-service', internal: true },
];

const linkStyle = {
  color: 'rgba(255,255,255,0.35)',
  fontFamily: "'DM Sans',sans-serif",
  fontSize: '0.85rem',
} as const;

/**
 * Shared PadelDistrict footer rendered by (tenant)/layout.tsx across every
 * route. Extracted from the landing page's inline <Footer> so sub-routes get
 * the brand footer too.
 */
export function PadelDistrictFooter() {
  return (
    <div className="padeldistrict-root pd-chrome">
      <footer
        style={{
          backgroundColor: '#000',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="container py-14">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            <div className="md:col-span-2">
              <Logo className="h-7 w-auto mb-5" />
              <p
                style={{
                  color: 'rgba(255,255,255,0.35)',
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: '0.85rem',
                  lineHeight: 1.7,
                  maxWidth: '280px',
                }}
              >
                Saudi Arabia&apos;s premium padel destination — now in Jeddah,
                coming soon to Riyadh.
              </p>
              <div className="mt-5 flex items-center gap-4">
                <a
                  href="https://www.instagram.com/padeldistrict.ksa"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'rgba(255,255,255,0.35)',
                    fontFamily: "'DM Sans',sans-serif",
                    fontSize: '0.75rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                  }}
                  className="hover:text-white transition-colors"
                >
                  Instagram
                </a>
              </div>
            </div>

            <div>
              <div
                className="section-label mb-5"
                style={{ color: 'rgba(255,255,255,0.25)' }}
              >
                Navigate
              </div>
              <div className="flex flex-col gap-3">
                {navigateLinks.map((link) =>
                  link.internal ? (
                    <Link
                      key={link.label}
                      href={link.href}
                      style={linkStyle}
                      className="hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      style={linkStyle}
                      className="hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  ),
                )}
              </div>
            </div>

            <div>
              <div
                className="section-label mb-5"
                style={{ color: 'rgba(255,255,255,0.25)' }}
              >
                Connect
              </div>
              <div className="flex flex-col gap-3">
                {connectLinks.map((link) =>
                  link.internal ? (
                    <Link
                      key={link.label}
                      href={link.href}
                      style={linkStyle}
                      className="hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={linkStyle}
                      className="hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  ),
                )}
              </div>
            </div>
          </div>

          <div
            className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
          >
            <span
              style={{
                color: 'rgba(255,255,255,0.2)',
                fontFamily: "'DM Sans',sans-serif",
                fontSize: '0.75rem',
              }}
            >
              © 2026 Padel District. All rights reserved. Saudi Arabia.
            </span>
            <div className="flex items-center gap-1.5">
              <div
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#015EFF',
                }}
              />
              <span
                style={{
                  color: 'rgba(255,255,255,0.2)',
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: '0.75rem',
                }}
              >
                Powered by askflow.sa
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
